import { EventEmitter, Injectable, Optional } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  sendEmailVerification,
  fetchSignInMethodsForEmail
} from '@angular/fire/auth';
import { FirebaseError } from 'firebase/app';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';
import { AuthState, create_user, new_user } from './auth.model';
import jwt_decode from 'jwt-decode';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/services/toast/toast.service';

import { PlatformService } from '../../services/platform/platform.service';
// Web
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
// Mobile
import {
  PushNotificationSchema,
  PushNotifications,
  Token,
} from '@capacitor/push-notifications';
import { usersList } from 'src/app/views/panda-connect/panda-connect.model';
import { user } from 'src/app/views/profile/profile.model';
import { MenuService } from '../menu/menu.service';
import { AlertController, ModalController } from '@ionic/angular';
import { ImageSliderModelComponent } from 'src/app/components/image-slider-model/image-slider-model.component';
import { AciScoreDetailsComponent } from 'src/app/components/aci-score-details/aci-score-details.component';
import { Calendar } from '@awesome-cordova-plugins/calendar/ngx';
import moment from 'moment';
import { event_occurrences } from 'src/app/components/calendar/calendar.model';
import { CommonLoadingComponent } from 'src/app/components/common-loading/common-loading.component';
import { clearPostLoginRedirect, consumePostLoginRedirect } from './post-login-redirect';

const initialAuthState: AuthState = {
  isInitialized: false,
  isLoggedIn: false,
  isEmailVerified: false,
  id: null,
  email: null,
  name: null,
  phone_number: null,
  token: null,
  display_picture: null,
  cover_photo: null,
  role: null,
  age: null,
  is_parent: null
};
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  //Loaders
  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly isLoading$ = this.isLoading.asObservable();

    private isGetingAdmin: BehaviorSubject<any> = new BehaviorSubject<any>(
    false
  );
  readonly isGetingAdmin$ = this.isGetingAdmin.asObservable();


  private isLoading_users: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly isLoading_users$ = this.isLoading_users.asObservable();

  private openRegisterationFormm: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly openRegisterationFormm$ = this.openRegisterationFormm.asObservable();

  
  //Auth State Management
  private readonly _authState = new BehaviorSubject<AuthState>(
    initialAuthState
  );
  readonly auth$ = this._authState.asObservable();

  private users: BehaviorSubject<usersList> = new BehaviorSubject<usersList>(
    null
  );
  readonly users$: Observable<usersList> = this.users.asObservable();

  private verificationSentToUser: BehaviorSubject<user> = new BehaviorSubject<user>(
    null
  );
  readonly verificationSentToUser$: Observable<user> = this.verificationSentToUser.asObservable();


  private is_aci_loading: BehaviorSubject<boolean> =
  new BehaviorSubject<boolean>(false);
readonly is_aci_loading$: Observable<boolean> =
  this.is_aci_loading.asObservable();

  private user_aci: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  readonly user_aci$: Observable<any> = this.user_aci.asObservable();

 private user_mfa_info: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  readonly user_mfa_info$: Observable<any> = this.user_mfa_info.asObservable();



  forumChatEmitter = new EventEmitter<boolean>();


  openNotifications$: Subject<any> = new Subject<any>();
  notificationPayload$: Subject<any> = new Subject<any>();
  token: string;
  broadcast = new BroadcastChannel('FCMChannel');
  isMobileDevice: boolean;
  loggedOutSession: any;
  age: boolean;

  private show_email_verifications: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly show_email_verifications$: Observable<boolean> = this.show_email_verifications.asObservable();

  private show_otp_verifications: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly show_otp_verifications$: Observable<boolean> = this.show_otp_verifications.asObservable();

  private show_mfa_suggetion: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly show_mfa_suggetion$: Observable<boolean> = this.show_mfa_suggetion.asObservable();

  constructor(
    @Optional() private auth: Auth,
    private http: HttpClient,
    private toast: ToastService,
    private router: Router,
    private platform: PlatformService,
    private menu: MenuService,
    private modal: ModalController,
    private alert: AlertController,
    private calendar?: Calendar
  ) {
    this.isMobileDevice = this.platform.isMobile();
    this.auth.onIdTokenChanged((user) => {
      if (user) {
        user.getIdToken(true).then((token) => {
          const token_decoded: any = jwt_decode(token);
          let u: AuthState = {
            isLoggedIn: false,
            isEmailVerified: user.emailVerified,
            id: user.uid,
            email: user.email,
            name: user.displayName,
            phone_number: user.phoneNumber,
            token: token,
            display_picture: user.photoURL,
            role: token_decoded.role,
            cover_photo: token_decoded.cover_photo,
            is_admin: token_decoded.is_admin,
            age: token_decoded.age,
            is_parent: token_decoded.is_parent,
          };
          this._authState.next(u);
          this.token = u.token;
          this.age = token_decoded.age;
          this.token && this.requestPermission(u.token);
        });
      }
    });

    this.auth.onAuthStateChanged((user) => {
      if (!user) {
        this.isLoading.next(false);
        this._authState.next({...initialAuthState, isInitialized: true});
        return;
      }

      user.getIdToken(true).then((token) => {
        const token_decoded: any = jwt_decode(token);
        let u: AuthState = {
          isInitialized: true,
          isLoggedIn: true,
          isEmailVerified: user.emailVerified,
          id: user.uid,
          email: user.email,
          name: user.displayName,
          phone_number: user.phoneNumber,
          token: token,
          display_picture: user.photoURL,
          role: token_decoded.role,
          cover_photo: token_decoded.cover_photo,
        };
        this._authState.next(u);
        this.token = u.token;
        this.token && this.requestPermission(u.token);
      });
    });
  }

  toggleEmailVerification(state: boolean) {
    this.show_email_verifications.next(state);
  }

  toggleRegisterationForm(state: boolean) {
    this.openRegisterationFormm.next(state);
  } 

  toggleOtpVerification(state: boolean) {
    this.show_otp_verifications.next(state);
  }

  toggleMFASuggetion(state: boolean) {
    this.show_mfa_suggetion.next(state);
    if (!state) {
      this.router.navigate(['home'], { replaceUrl: true, queryParams: { fromLogin: true } });
    }
  }

  setMFAPreference(enableMfa: boolean, phoneNumber: string) {
    console.log("phone number", phoneNumber);
    // return;
    this.isLoading.next(true);
    this.patch(`user?type=update_mfa`, { is_mfa_enabled: enableMfa, phone_no: phoneNumber }, this.token)
      .subscribe(
        async (res) => {
          this.isLoading.next(false);
          this.show_mfa_suggetion.next(false);
          if (enableMfa) {
            this.toast.showToast(
              'MFA Enabled',
              'We have sent a verification code to your  phone number and email as well, please verify.',
              'success'
            );
            this.toggleOtpVerification(true);
          } else {
            this.router.navigate(['home'], { replaceUrl: true, queryParams: { fromLogin: true } });
          }
        },
        async (err) => {
          this.isLoading.next(false);
          this.toast.showToast(
            'Error',
            'There was an issue saving your MFA preference. Please try again shortly.',
            'danger'
          );
          this.show_mfa_suggetion.next(false);
          this.router.navigate(['home'], { replaceUrl: true, queryParams: { fromLogin: true } });
        }
      );
  }


  async sendEmailVerifications() {
    this.isLoading.next(true);
    sendEmailVerification(this.loggedOutSession.user)
      .then(async (data) => {
        this.isLoading.next(false);
        this.toast.showToast(
          'Verification sent',
          'We have sent a verification email, please verify your email to login.'
        );
        this.show_email_verifications.next(false);
      })
      .catch(async (err: FirebaseError) => {
        this.isLoading.next(false);
        this.toast.showToast(
          `Error`,
          'There was a problem with sending you an email. Try again later or contact our support team.'
        );
      });
  }

  async sendEmailVerificationsToClubUser(user) {
    this.isLoading.next(true);
    sendPasswordResetEmail(this.auth, user.email)
    .then(async (response) => {
      this.get_users(user.email, 1, 100, true, true);
      this.isLoading.next(false);
      this.toast.showToast('', 'We have sent an email for verification to your athlete, please ask him/her to verify and set new password to login.', '');
    })
    .catch(async (err: any) => {
      this.isLoading.next(false);
      if (err.code === 'auth/invalid-email') {
        this.toast.showToast(
          '',
          'Your email address has bad email formatting. Please correct and try again.'
        );
      } else if (err.code === 'auth/user-not-found') {
        this.toast.showToast(
          'User not found',
          'Please make sure your email is correct and try again.'
        );
      }
    });
  }

  updateDisplayPicture(display_picture: string) {
    let currentAuthState = this._authState.value;
    currentAuthState.display_picture = display_picture;
    this._authState.next(currentAuthState);
  }

  fetchSignInMethodsForEmail(email, controls, controlKey) {
    let error = {
      emailAlreadyUsed: false,
      emailAlreadyExists: false
    }
    Object.keys(controls).forEach(key => {
      if (controlKey != key) {
        if (email === controls[key].value) {
          error.emailAlreadyUsed = true;
        }
      }
    });
    return new Promise(resolve => {
      fetchSignInMethodsForEmail(this.auth, email).then(res => {
        if (res.length > 0) {
          error.emailAlreadyExists = true;
          resolve(error);
        } else {
          if (!error.emailAlreadyExists && !error.emailAlreadyUsed) {
            resolve(null);
          } else {
            resolve(error);
          }
        }
      });
    });
  }

  emailVerification(email) {
    this.post_unauth(`payment?type=email_verification&email=${email}`, {}, '')
      .pipe(take(1))
      .subscribe(
        async (res) => {
          console.log("set referral response", res);
        },
        async (err) => {
          console.log("set referral error", err)
          this.isLoading.next(false);
        }
      );
  }

    sendWelcomeEmail(email) {
    this.post_unauth(`contracts?type=send_welcome_email`, {
      email: email
    }, '')
      .pipe(take(1))
      .subscribe(
        async (res) => {
          console.log("welcome email sent successfully", res);
        },
        async (err) => {
          console.log("set referral error", err)
          this.isLoading.next(false);
        }
      );
  }

   public get_users(search: string, page: number, limit: number, registrationClub :boolean, newCreatedUser = false, field_admin = false) {
    this.isLoading_users.next(true);
    if(registrationClub) {
      this.toggleRegisterationForm(false);
    }

    let url = `parent?type=search_users&page=${page}&limit=${limit}`;

    if (search) {
      url += `&search=${search}`;
    }

    if (registrationClub) {
      url += `&registration_club=true`;
    }

    if (field_admin) {
      url += `&field_admin=true`;
    }

    this
      .get(url)
      .pipe(take(1))
      .subscribe(
        (res: any) => {
          if(res?.users[0]?.id) {
            this.toggleRegisterationForm(false);
            this.verificationSentToUser.next(res.users[0]);
          }
          this.users.next(res);
          this.isLoading_users.next(false);
        },
        (err) => {
          console.log('err', err);
          this.users.next(null);
          this.isLoading_users.next(false);
        }
      );
  }

  clearVerificationSentToUser() {
    this.verificationSentToUser.next(null);
  }

  async registerAccountWithEmailPassword(
    user: create_user,
    is_parent?: boolean,
    child_id?: string,
    club_registeration = false
  ) {
    this.isLoading.next(true);
    createUserWithEmailAndPassword(this.auth, user.email, user.password)
      .then(async (data) => {
        data.user.getIdToken(true).then(async (token) => {
          const token_decoded: any = jwt_decode(token);
          if (!token_decoded.role) {
            let u: new_user = {
              uid: data.user.uid,
              name: user.name,
              dob: user.dob,
              weight: user.weight,
              gender: user.gender,
              email: data.user.email,
              role: user.role,
              is_parent: is_parent || false,
              child_id: child_id || '',
              referral: user.referral,
              phone_number: user.phone_number
            };
            await this.post('auth?type=new-user', u, token)
              .pipe(take(1))
              .subscribe(
                async (res) => {
                  if (res) {
                    this.loggedOutSession = data;
                    this.isLoading.next(false);
                    await data.user.getIdToken(true);
                    this.sendEmailVerifications();
                    // this.router.navigate(['auth/login'], { replaceUrl: true });
                    // signOut(this.auth);

                    if (club_registeration) {
                      this.get_users(user.email, 1, 100, true);
                    } else {
                      this.router.navigate(['auth/login'], { replaceUrl: true });
                      localStorage.removeItem('age');
                      signOut(this.auth);
                    }
                  }
                },
                async (err) => {
                  this.toast.showToast(
                    'Error',
                    'There was an issue creating this new user. Please try again shortly.',
                    'danger'
                  );
                  this.isLoading.next(false);
                }
              );
          }
        });
      })
      .catch(async (err: FirebaseError) => {
        this.isLoading.next(false);
        if (err.code === 'auth/email-already-in-use') {
          this.toast.showToast(
            'User already exists',
            'A user with this email address already exists, please log in instead of creating a new account.'
          );
        } else {
          this.toast.showToast(
            `Error: ${err.code}`,
            'There was a problem creating your new account. Try again later or contact our support team.'
          );
        }
      });
  }



  calculateAge(birthdate: Date): number {
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    localStorage.setItem('age', age.toString());
    return age;
  }


  async openACIModel() {

    const m = await this.modal.create({
      component: AciScoreDetailsComponent,
      animated: true,
      backdropDismiss: true,
      cssClass: 'aciModel'
    });
    m.onDidDismiss().then(data => {
    });
    await m.present();
  }

  async openQuickStartModel(role) {
    const m = await this.modal.create({
      component: ImageSliderModelComponent,
      animated: true,
      backdropDismiss: true,
      cssClass: 'sliderModel'
    });
    m.onDidDismiss().then(data => {
      if (role == "athlete") {
        this.openACIModel();
      }
    });
    await m.present();
  }

  async post_feature_reviewed() {
    await this.post('auth?type=features-reviewed', {}, this.token)
      .pipe(take(1))
      .subscribe(
        async (res) => {
        },
        async (err) => {
        }
      );
  }

  verify_under_age() {
    this.is_aci_loading.next(true);
    this
      .get('reports?type=get_user_aci').pipe(take(1)).subscribe(
        async (res: any) => {
          if (this.calculateAge(res?.is_features_reviewed?.dob) <= 12) {
            this.menu.disableRoutes('age_restrictions');
          }
          //  else {
          //   this.router.navigate(['home'], { replaceUrl: true, queryParams: { fromLogin: true } });
          // }
          // this.user_aci.next(res);
          this.is_aci_loading.next(false);
          // if (role == "athlete") {
          //   if (!res?.is_features_reviewed?.is_features_reviewed) {
          //     this.openQuickStartModel(role);
          //     this.post_feature_reviewed();
          //   } else {
          //     if (this.router.url === '/profile/0?fromLogin=true' || this.router.url === '/profile/0' || this.router.url === '/home' || this.router.url === '/home?fromLogin=true') {
          //       this.openACIModel();
          //     }
          //   }
          // } else if ((!res?.is_features_reviewed?.is_features_reviewed) && fromLogin) {
          //   this.openQuickStartModel(role);
          //   this.post_feature_reviewed();
          // }
        },
        (err) => {
          this.is_aci_loading.next(false);
        }
      );
  }

  public get_ad_admin() {
    this.is_aci_loading.next(true);
    this.get('reports?type=get_user_aci').pipe(take(1)).subscribe(
        async (res: any) => {
          if(res?.is_features_reviewed?.ad_admin_id) {
            localStorage.setItem('ad_admin_id', res?.is_features_reviewed?.ad_admin_id);
            localStorage.setItem('ad_admin_email', res?.is_features_reviewed?.ad_admin_email);
            this.isGetingAdmin.next(res?.is_features_reviewed?.ad_admin_id);
          } else {
            localStorage.removeItem('ad_admin_id');
            localStorage.removeItem('ad_admin_email');
            localStorage.removeItem('selected_account');
            localStorage.removeItem('using_sub_admin');
          }
        },
        (err) => {
          this.is_aci_loading.next(false);
        }
      );
  }

  public get_user_aci(role) {
        console.log("admin  new api")

    this.is_aci_loading.next(true);
    this.get('reports?type=get_user_aci').pipe(take(1)).subscribe(
        async (res: any) => {
          let replaceUrl = localStorage.getItem("club_registeration_link");
          // Deep link saved before sign-in (e.g. assigner -> Manage Referee Assignment).
          const postLoginUrl = consumePostLoginRedirect();
          if(res?.is_features_reviewed?.ad_admin_id) {
            localStorage.setItem('ad_admin_id', res?.is_features_reviewed?.ad_admin_id);
            localStorage.setItem('ad_admin_email', res?.is_features_reviewed?.ad_admin_email);
          }

          if (postLoginUrl) {
            this.router.navigateByUrl(postLoginUrl, { replaceUrl: true });
            this.menu.showMenu();
          } else if (this.calculateAge(res?.is_features_reviewed?.dob) <= 12) {
            this.menu.disableRoutes('age_restrictions');
            if(replaceUrl) {
              this.router.navigate([`${replaceUrl}`]);
              this.menu.showMenu();
            } else {
              this.router.navigate(['profile/0'], { replaceUrl: true });
            }
          } else {
            if(replaceUrl) {
              this.router.navigate([`${replaceUrl}`]);
              this.menu.showMenu();
            } else {
              this.router.navigate(['home'], { replaceUrl: true });
            }
          }
          this.user_aci.next(res);
          this.is_aci_loading.next(false);
          // if (role == "athlete") {
            if (!res?.is_features_reviewed?.is_features_reviewed) {
              this.openQuickStartModel(role);
              this.post_feature_reviewed();
            } else {
              // if (this.router.url === '/profile/0?fromLogin=true' || this.router.url === '/profile/0' || this.router.url === '/home' || this.router.url === '/home?fromLogin=true') {
                this.openACIModel();
              // }
            }
          // } else if ((!res?.is_features_reviewed?.is_features_reviewed) && fromLogin) {
          //   this.openQuickStartModel(role);
          //   this.post_feature_reviewed();
          // }
        },
        (err) => {
          this.is_aci_loading.next(false);
        }
      );
  }





  verifyOTP(otp: string) {
    this.isLoading.next(true);
    let objTest = {mfa_code_entered: otp, email: this.credentials?.email};
    this.post_unauth(`contracts?type=verify_otp`, {mfa_code_entered: otp, email: this.credentials?.email}, '')
      .pipe(take(1))
      .subscribe(
        async (res) => {
          this.isLoading.next(false);
          if (res && res?.message == 'MFA code verified successfully.') {
            this.loginWithEmailPassword(this.credentials.email, this.credentials.password);
            this.toggleOtpVerification(false);
            this.toast.showToast('OTP Verified', 'Your OTP has been successfully verified.', 'success');
          } else {
            this.toggleOtpVerification(false);
          }
        },
        async (err) => {
          this.isLoading.next(false);
          this.toggleOtpVerification(false);
          this.toast.showToast('OTP Verification Failed', 'The OTP you entered is incorrect. Please try again.', 'danger');
          // this.toast.showToast('Error', 'There was an issue verifying the OTP. Please try again later.', 'danger');
        }
      );
  }


  credentials = {
    email: '',
    password: ''
  }

  resendOTP() {
    let { email, password } = this.credentials;

    this.isLoading.next(true);
    this.get_without_base(`contracts?type=check_and_send_otp&email=${email}`)
      .pipe(take(1)).subscribe(
        async (res: any) => {
          this.user_mfa_info.next(res);
          this.isLoading.next(false);
          this.toast.showToast('OTP Sent', 'A new OTP has been sent to your phone number and email.', 'success');
        },
        async (err) => {
          this.isLoading.next(false);
          this.toast.showToast('Error', 'There was an issue fetching MFA info. Please try again later.', 'danger');
        }
      );


  }

  async checkMFAAuth(email: string, password: string) {
    this.credentials.email = email;
    this.credentials.password = password;

    this.isLoading.next(true);
    this.get_without_base(`contracts?type=check_and_send_otp&email=${email}`)
      .pipe(take(1)).subscribe(
        async (res: any) => {
          this.user_mfa_info.next(res);
          this.isLoading.next(false);
          if (res && res.is_mfa_enabled) {
            this.toggleOtpVerification(true);
            this.toast.showToast('MFA Info', 'Please verify code sent to your phone number or email to continue.', 'success');
          } else {
            this.toggleMFASuggetion(true);
            this.loginWithEmailPassword(email, password, true);
          }
        },
        async (err) => {
          this.isLoading.next(false);
          this.toggleOtpVerification(false);
          this.toast.showToast('Error', 'There was an issue fetching MFA info. Please try again later.', 'danger');
        }
      );


  }

  get_without_base(url: string): Observable<any> {
    const headers = {
      'Content-Type': 'application/json'
    }
    return this.http.get(environment.unauth_api_url + url, {
      headers: new HttpHeaders(headers),
    });
  }



  async loginWithEmailPassword(email: string, password: string, is_mfa_disabled = false) {
    this.isLoading.next(true);
    signInWithEmailAndPassword(this.auth, email, password)
      .then((user: any) => {    
        this.token = user.user.accessToken;  
        this.isLoading.next(false);
        if(!user.user.emailVerified) {
          this.toast.showToast(
            'Email verification required',
            'Your email is not verified, please verify it to continue.'
          );
          this.loggedOutSession = user;
          signOut(this.auth);
          this.show_email_verifications.next(true);
        } else {
          const token_decoded: any = jwt_decode(user.user.accessToken);
            // this.get_user_aci(token_decoded.role);
            if(token_decoded.age) {
              // this.router.navigate(['home'], { replaceUrl: true});
              if (!is_mfa_disabled) {
                this.router.navigate(['home'], { replaceUrl: true, queryParams: { fromLogin: true}});
              }
            } else {
              this.router.navigate(['profile/0'], { replaceUrl: true, queryParams: { fromLogin: true}});
              // this.router.navigate(['profile/0'], { replaceUrl: true});
            }
        }
      })
      .catch(async (err: FirebaseError) => {
        this.isLoading.next(false);
        if (err.code === 'auth/wrong-password') {
          this.toast.showToast(
            'Wrong Password',
            'Password provided does not match with the identity already registered. Please add the valid password and try again.'
          );
        } else if (err.code === 'auth/user-not-found') {
          this.toast.showToast(
            'No user found',
            'With provided credentials, we could not find any user registered. Please sign up your new account and try again.'
          );
        } else if (err.code === 'auth/user-disabled') {
          this.toast.showToast(
            'Account Blocked',
            'Your account is blocked by PANDAStronger moderation team. Please contact support to verify and enable it.'
          );
        }
      });
  }

  async logout() {
    localStorage.removeItem('age');
    this.updatePushNotificationId('', this.token);
    await signOut(this.auth);
    localStorage.removeItem('club_registeration_link');
    clearPostLoginRedirect();
    localStorage.removeItem('ad_admin_id');
    localStorage.removeItem('ad_admin_email');
    localStorage.removeItem('selected_account');
    localStorage.removeItem('using_sub_admin');
  }

  private post_unauth(url: string, body: any, token: string): Observable<any> {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    return this.http.post(environment.unauth_api_url + url, body, {
      headers: new HttpHeaders(headers),
    });
  }

  private patch(url: string, body: any, token: string): Observable<any> {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    return this.http.patch(environment.api_url + url, body, {
      headers: new HttpHeaders(headers),
    });
  }

  private post(url: string, body: any, token: string): Observable<any> {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    return this.http.post(environment.api_url + url, body, {
      headers: new HttpHeaders(headers),
    });
  }

  get(url: string): Observable<any> {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: this.token,
    };
    return this.http.get(environment.api_url + url, {
      headers: new HttpHeaders(headers),
    });
  }

  async forgotPassword(email: string) {
    return sendPasswordResetEmail(this.auth, email)
      .then(async (response) => {
        // console.log('response', response);
        this.toast.showToast('', 'Please Check your email for reset link.', '');
      })
      .catch(async (err: any) => {
        if (err.code === 'auth/invalid-email') {
          this.toast.showToast(
            '',
            'Your email address has bad email formatting. Please correct and try again.'
          );
        } else if (err.code === 'auth/user-not-found') {
          this.toast.showToast(
            'User not found',
            'Please make sure your email is correct and try again.'
          );
        }
      });
  }

  // Notification Utilities
  requestPermission(token) {
    if (this.isMobileDevice) {
      PushNotifications.requestPermissions().then((result) => {
        if (result.receive === 'granted') {
          // Register with Apple / Google to receive push via APNS/FCM
          PushNotifications.register();
          PushNotifications.addListener(
            'registration',
            (currentToken: Token) => {
              this.updatePushNotificationId(currentToken.value, token);
            }
          );
        } else {
          PushNotifications.addListener('registrationError', (error: any) => {
            console.log('Error on registration: ', JSON.stringify(error));
          });
          // Show some error
        }
      });
    } else {
      const messaging = getMessaging();

      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          getToken(messaging, { vapidKey: environment.firebase.vapidKey })
            .then((currentToken) => {
              if (currentToken) {
                if (token) {
                  this.updatePushNotificationId(currentToken, token);
                }
              } else {
                console.log('Please request permission to generate token.');
              }
            })
            .catch((err) => {
              console.log('An error occurred while retrieving token. ', err);
            });
        }
      });
    }
  }

  closeNotifications() {
    this.openNotifications$.next(null);
  }

  goToGroups(data) {
    this.router.navigate([`teaming-up/group/${data.teamingUP_id}`], {
      queryParams: { admin_id: data.admin_id, group: data.group, current_user_role: data.role, tab: 'forum', stopQuickMenue: true },
    });
  }

  goToGroupCalendar(data) {
    this.router.navigate([`teaming-up/group/${data.teamingUP_id}`], {
      queryParams: { admin_id: data.admin_id, group: data.group, current_user_role: data.role, tab: 'calendar', stopQuickMenue: true },
    });
  }

  goToGroupsParent(data) {
    this.router.navigate([`teaming-up/group/${data.teamingUP_id}`], {
      queryParams: { admin_id: data.admin_id, group: data.group, current_user_role: data.role, tab: 'parent_forum', stopQuickMenue: true },
    });
  }

  goToGroupsChat(data) {
    this.router.navigate([`teaming-up/group/${data.teamingUP_id}`], {
      queryParams: { admin_id: data.admin_id, group: data.chat_group_name, current_user_role: data.role, tab: 'group_chat', channel_id: data.teaming_up_channel_id, stopQuickMenue: true },
    });
  }

  goToGroupNotices(data) {
    this.router.navigate([`teaming-up/group/${data.teamingUP_id}`], {
      queryParams: { admin_id: data.admin_id, group: data.group, current_user_role: this.auth.currentUser, tab: 'notice', stopQuickMenue: true },
    });
  }

  listen() {
    if (this.isMobileDevice) {
      let payload: any = {
        notification: {
          title: '',
        },
        data: {},
      };
      PushNotifications.addListener(
        'pushNotificationReceived',
        (pushNotificationPayload: PushNotificationSchema) => {
          payload.notification.title = pushNotificationPayload.title;
          payload.data = pushNotificationPayload.data;
          this.notificationPayload$.next(payload);
        }
      );

      // Method called when tapping on a notification
      PushNotifications.addListener('pushNotificationActionPerformed',
        (notification: any) => {
          if (notification?.notification?.data?.tab === 'forum') {
            // if (window.location.pathname.split('/')[2] === 'group') {
            //   this.router.navigate(['/teaming-up'], { queryParams: { stopQuickMenue: true} })
            // }
            this.router.navigate(['/home'], { queryParams: { stopQuickMenue: true} })
            setTimeout(() => {
              if(notification?.notification?.data.teamingUP_id && notification?.notification?.data.redirect_to == 'calendar' && this.isMobileDevice) {
                this.auth$.pipe(skipWhile(us => !us.token), take(1)).subscribe((user) => {
                  this.token = user.token;
                  this.get_group_events_and_sync(notification?.notification?.data.teamingUP_id);
                });
              }
              if (notification?.notification?.data.redirect_to == 'calendar') {
                this.goToGroupCalendar(notification?.notification?.data);
              } else {
                this.goToGroups(notification?.notification?.data);
              }
            }, 3000);
          } else if (notification?.notification?.data?.tab === 'parent_forum') {
            // if (window.location.pathname.split('/')[2] === 'group') {
            //   this.router.navigate(['/teaming-up'], { queryParams: { stopQuickMenue: true} })
            // }
            this.router.navigate(['/home'], { queryParams: { stopQuickMenue: true} })
            setTimeout(() => {
              this.goToGroupsParent(notification?.notification?.data);
            }, 3000);
          }
          else if (notification?.notification?.data?.tab === 'group_chat') { // for new group chat
            // if (window.location.pathname.split('/')[2] === 'group') {
            //   this.router.navigate(['/teaming-up'], { queryParams: { stopQuickMenue: true} })
            // }
            this.router.navigate(['/home'], { queryParams: { stopQuickMenue: true} })
            setTimeout(() => {
              this.goToGroupsChat(notification?.notification?.data);
            }, 3000);
          } else if (notification?.notification?.data?.tab === 'notice') {
            // if (window.location.pathname.split('/')[2] === 'group') {
            //   this.router.navigate(['/teaming-up'], { queryParams: { stopQuickMenue: true} })
            // }
            this.router.navigate(['/home'], { queryParams: { stopQuickMenue: true} })
            setTimeout(() => {
              this.goToGroupNotices(notification?.notification?.data);
            }, 3000);
          } else if (notification?.notification?.data?.redirect_to === 'notification') {
            this.openNotifications$.next({ open: true });
            this.router.navigate(['profile/0'], { replaceUrl: true, queryParams: { stopQuickMenue: true} });
            this.router.navigate(['home'], { replaceUrl: true, queryParams: { stopQuickMenue: true} });
          } else if (notification?.notification?.data?.redirect_to === 'home') {
            this.router.navigate(['home'], { replaceUrl: true, queryParams: { stopQuickMenue: true} });
          } else if (notification?.notification?.data?.redirect_to === 'calendar') {
            this.router.navigate(['/calendar'], { replaceUrl: true, queryParams: { stopQuickMenue: true} });
          } else if (notification?.notification?.data?.redirect_to === 'chat') {
            this.router.navigate(['/chat'], { replaceUrl: true, queryParams: { stopQuickMenue: true} });
          } else if (notification?.notification?.data?.redirect_to === 'profile-workout') {
            // if (window.location.pathname.split('/')[1] === 'profile') {
            //   this.router.navigate(['/home'], {queryParams: { stopQuickMenue: true} });
            // }

            this.router.navigate(['/home'], { queryParams: { stopQuickMenue: true} })
            setTimeout(() => {
              this.redirectToUserWorkouts(notification?.notification?.data?.user_id);
            }, 3000);
          } else if (notification?.notification?.data?.redirect_to === 'challenge_created') {
            this.router.navigate(['/challenge'], { replaceUrl: true, queryParams: { stopQuickMenue: true} });
          } else if (notification?.notification?.data?.redirect_to === 'club_report') {
            this.router.navigate(['/panda-club/registrar-report/'+payload.data.club_id]);
          } else if (notification?.notification?.data?.redirect_to === 'panda-club') {
            this.router.navigate(['/panda-club']);
          }
        }
      );

    } else {
      const messaging = getMessaging();

      // Background
      this.broadcast.onmessage = (event) => {
        console.log(event);
        this.notificationPayload$.next(event.data);
      };

      // Foreground
      onMessage(messaging, (payload) => {
        console.log(payload);
        this.notificationPayload$.next(payload);
      });
    }
  }

  redirectToUserWorkouts(id) {
    this.router.navigate([`/profile/${id}`], {
      queryParams: { workout: true, stopQuickMenue: true },
    });
  }

  updatePushNotificationId(push_notification_id, token?) {
    // console.log('push_notification_id', push_notification_id);
    let url = `user?type=push_notification_id`;
    this.post(
      url,
      {
        id: push_notification_id,
      },
      token
    ).pipe(take(1)).subscribe(
      (response) => {
      },
      (err) => {
        console.log('err', err);
      }
    );
  }

  async refreshToken() {
    const user = this.auth.currentUser;
    const token = await user.getIdToken(true);

    let currentAuthState = this._authState.value;
    currentAuthState.token = token;
    this._authState.next(currentAuthState);
    // console.log('token', token);
  }

  // calendar sync functions
  async get_group_events_and_sync(teaming_up_group_id?: string) {
    // this.isLoading.next(true);
    this.openCommonLoading();
    let url = `events?start_date=${moment(new Date()).subtract(2, 'day').format('YYYY-MM-DD').toString()}&end_date=${moment(new Date()).add(2, 'year').format('YYYY-MM-DD').toString()}&timezone=${moment.tz.guess()}&teaming_up_group_id=${teaming_up_group_id}`;
    this.get(url).pipe(take(1)).subscribe(async (schedules: event_occurrences[]) => {
        this.create_calendar_events(teaming_up_group_id, schedules);
    }, (err) => {
        // this.isLoading.next(false);
        this.modal.dismiss();
    });
}

async getExplicitPermissionsFromCalendar(id, events): Promise<boolean> {
  return new Promise<boolean>(async (resolve) => {
      let p = await this.calendar.hasReadWritePermission();
      if (!p) {
          this.confirm_calendar_synch(id, events)
          await this.calendar.requestReadWritePermission();
          resolve(true);
      }
      resolve(true);
  });
}

async confirm_calendar_synch(id, events) {
  const a = await this.alert.create({
    animated: true,
    backdropDismiss: true,
    header: 'Calendar Sync',
    message: `Are you sure you want to sync team activities with your local calendar?`,
    buttons: [{
      text: 'Yes',
      handler: () => {
        this.create_calendar_events(id, events);
      }
    }, {
      
      text: 'No, cancel',
      role: 'cancel',
      handler: () => {
        this.modal.dismiss();
      }
    }]
  });

  await a.present();
}

async create_calendar_events(id, events) {
  try {
      await this.getExplicitPermissionsFromCalendar(id, events);
      const calendarName = 'PANDA Stronger Events';
      let _calendars = await this.calendar.listCalendars();

      if (_calendars.length > 0) {
          let arr_calendarExists = _calendars.filter(v => v.name === calendarName);
          if (arr_calendarExists.length < 1) {
              await this.calendar.createCalendar(calendarName);
          }
      }

      let localEvents = await this.calendar.findEventWithOptions(null, null, null, new Date(moment(new Date()).subtract(2, 'day').toString()), new Date(moment(new Date()).add(2, 'year').toString()), {
          calendarName: calendarName
      });

      for await (let e of events) {
          let currentEvent = localEvents.length > 0 ? localEvents.filter(ev => ev.message.split('_')[0] === e.event_occurrence_id) : [];

          if (currentEvent.length > 0) {
              try {
                  await this.calendar.deleteEventById(currentEvent[0].id);
              } catch (err) {
                  console.log(err);
              }
          }
          await this.calendar.createEventWithOptions(e.title, null, `${e.event_occurrence_id}_${e.teaming_up_group_id}`, new Date(e.start), new Date(e.end), {
              calendarName: calendarName
          })
      };

      localEvents = await this.calendar.findEventWithOptions(null, null, null, new Date(moment(new Date()).subtract(2, 'day').toString()), new Date(moment(new Date()).add(2, 'year').toString()), {
          calendarName: calendarName
      });

      if (localEvents.length > 0) {
          let dbEventIDs: Set<string> = new Set(events.map(e => e.event_occurrence_id));
          let localEventIDs: Set<string> = new Set(localEvents.filter(v => v.message.split('_')[1] === id).map(l => l.message.split('_')[0]));

          const staleEvents = [...localEventIDs].filter(item => !dbEventIDs.has(item));

          for await (let s of staleEvents) {
              try {
                  let currentEvent = localEvents.length > 0 ? localEvents.filter(ev => ev.message.split('_')[0] === s) : [];
                  if (currentEvent.length > 0) {
                      await this.calendar.deleteEventById(currentEvent[0].id);
                  }
              }
              catch (err) {
                  console.log(err);
              }
          }
      }
      // this.isLoading.next(false);
      this.modal.dismiss();
      this.toast.showToast('Sync successfull', 'All of the events for this TeamingUp Group has been successfully synchronized with your local calendar.', 'success');
  }
  catch (err) {
    console.log("last errror here", err);
      // this.isLoading.next(false);
      this.modal.dismiss();
      this.toast.showToast('Sync error', 'There was an issue with local calendar sync. Please make sure you have provided the permission to access the calendar in your phone settings.', 'danger');
  }
}


async openCommonLoading() {
  // check that the logged in user is athlete and navigate from Login Page
    const m = await this.modal.create({
      component: CommonLoadingComponent,
      animated: true,
      backdropDismiss: true,
      cssClass: 'aciModel',
      componentProps: {
        message: 'Syncing group events to your phone calendar...'
      }
    });
    m.onDidDismiss().then(data => {
    });
    await m.present();
}




}
